import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  FileDown,
  Lock,
  RefreshCw,
  Unlock,
  Users,
} from "lucide-react";
import type { PayPeriodDetail } from "@crewledger/shared/schemas";
import {
  CommandCenterCard,
  EmptyState,
  PageHeader,
  StatCard,
  useToast,
} from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatCurrency, formatHours } from "@/features/dashboard/utils";
import {
  approvePayPeriod,
  exportPayPeriod,
  fetchPayPeriod,
  lockPayPeriod,
  recalculatePayPeriod,
  submitPayPeriodForReview,
  unlockPayPeriod,
} from "./api";
import { PayPeriodStatusBadge } from "./components/pay-period-status-badge";
import { usePayPeriodPermissions } from "./use-pay-period-permissions";

function BreakdownTable({
  title,
  rows,
  showPayroll,
}: {
  title: string;
  rows: PayPeriodDetail["workerBreakdown"];
  showPayroll: boolean;
}) {
  if (rows.length === 0) {
    return (
      <CommandCenterCard title={title}>
        <p className="text-sm text-muted-foreground">No data for this period.</p>
      </CommandCenterCard>
    );
  }

  return (
    <CommandCenterCard title={title}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="pb-2 font-medium">Name</th>
              <th className="pb-2 font-medium">Reg</th>
              <th className="pb-2 font-medium">OT</th>
              <th className="pb-2 font-medium">DT</th>
              {showPayroll ? <th className="pb-2 font-medium">Gross</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-border/50">
                <td className="py-2">
                  <span className="font-medium">{row.name}</span>
                  {row.code ? (
                    <span className="ml-1 text-xs text-muted-foreground">({row.code})</span>
                  ) : null}
                </td>
                <td className="py-2 font-mono text-xs">{formatHours(row.regularHours)}</td>
                <td className="py-2 font-mono text-xs">{formatHours(row.overtimeHours)}</td>
                <td className="py-2 font-mono text-xs">{formatHours(row.doubleTimeHours)}</td>
                {showPayroll ? (
                  <td className="py-2 font-mono text-xs">{formatCurrency(row.grossPay)}</td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CommandCenterCard>
  );
}

export function PayPeriodDetailPage() {
  const { payPeriodId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canViewPayroll, canApprove: canApproveRole } = usePayPeriodPermissions();
  const [period, setPeriod] = useState<PayPeriodDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");

  const load = () => {
    if (!payPeriodId) return;
    setLoading(true);
    fetchPayPeriod(payPeriodId)
      .then((res) => { setPeriod(res.data); })
      .catch((err: unknown) => { setError(err instanceof Error ? err.message : "Failed to load"); })
      .finally(() => { setLoading(false); });
  };

  useEffect(load, [payPeriodId]);

  const runAction = async (key: string, fn: () => Promise<unknown>, success: string) => {
    setActionLoading(key);
    try {
      await fn();
      toast({ variant: "success", title: success });
      load();
    } catch (err) {
      toast({
        variant: "error",
        title: "Action failed",
        description: err instanceof Error ? err.message : "Something went wrong",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnlock = async () => {
    if (!payPeriodId || !unlockReason.trim()) return;
    setActionLoading("unlock");
    try {
      await unlockPayPeriod(payPeriodId, { reason: unlockReason.trim() });
      toast({ variant: "success", title: "Pay period unlocked" });
      setUnlockOpen(false);
      setUnlockReason("");
      load();
    } catch (err) {
      toast({
        variant: "error",
        title: "Unlock failed",
        description: err instanceof Error ? err.message : "Something went wrong",
      });
    } finally {
      setActionLoading(null);
    }
  };

  if (error && !period) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="Pay period not found"
        description={error}
        actionLabel="Back to pay periods"
        onAction={() => navigate("/pay-periods")}
      />
    );
  }

  if (loading || !period) {
    return <div className="h-96 animate-pulse rounded-xl border bg-card-white" />;
  }

  const { totals } = period;

  return (
    <div className="space-y-6 lg:space-y-8">
      <PageHeader
        title={period.label}
        description={`${period.startDate} → ${period.endDate}`}
        badge={<PayPeriodStatusBadge status={period.status} />}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to={`/pay-periods/${period.id}/review`}>
              <Button variant="outline" size="sm">
                Review entries
              </Button>
            </Link>
            {period.canRecalculate ? (
              <Button
                variant="outline"
                size="sm"
                disabled={actionLoading === "recalculate"}
                onClick={() =>
                  runAction(
                    "recalculate",
                    () => recalculatePayPeriod(period.id, { async: true }),
                    "Recalculation queued",
                  )
                }
              >
                <RefreshCw className="mr-1.5 h-4 w-4" />
                Recalculate
              </Button>
            ) : null}
            {period.status === "OPEN" && canApproveRole ? (
              <Button
                size="sm"
                disabled={actionLoading === "submit"}
                onClick={() =>
                  runAction(
                    "submit",
                    () => submitPayPeriodForReview(period.id),
                    "Submitted for review",
                  )
                }
              >
                Submit for review
              </Button>
            ) : null}
            {period.canApprove ? (
              <Button
                size="sm"
                disabled={actionLoading === "approve"}
                onClick={() =>
                  runAction("approve", () => approvePayPeriod(period.id), "Pay period approved")
                }
              >
                <CheckCircle className="mr-1.5 h-4 w-4" />
                Approve
              </Button>
            ) : null}
            {period.canLock ? (
              <Button
                size="sm"
                disabled={actionLoading === "lock"}
                onClick={() =>
                  runAction("lock", () => lockPayPeriod(period.id), "Pay period locked")
                }
              >
                <Lock className="mr-1.5 h-4 w-4" />
                Lock
              </Button>
            ) : null}
            {period.canUnlock ? (
              <Button variant="outline" size="sm" onClick={() => { setUnlockOpen(true); }}>
                <Unlock className="mr-1.5 h-4 w-4" />
                Unlock
              </Button>
            ) : null}
            {period.canExport ? (
              <Button
                size="sm"
                disabled={actionLoading === "export"}
                onClick={() =>
                  runAction(
                    "export",
                    () => exportPayPeriod(period.id, { type: "PAYROLL" }),
                    "Export queued",
                  )
                }
              >
                <FileDown className="mr-1.5 h-4 w-4" />
                Export
              </Button>
            ) : null}
          </div>
        }
      />

      {totals.criticalExceptions > 0 ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {totals.criticalExceptions} critical exception
          {totals.criticalExceptions === 1 ? "" : "s"} must be resolved before locking.
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Workers" value={totals.totalWorkers} icon={Users} accent="navy" />
        <StatCard label="Regular hours" value={formatHours(totals.regularHours)} icon={Clock} accent="navy" />
        <StatCard label="OT hours" value={formatHours(totals.overtimeHours)} icon={Clock} accent="amber" />
        <StatCard label="DT hours" value={formatHours(totals.doubleTimeHours)} icon={Clock} accent="amber" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Holiday hours" value={formatHours(totals.holidayHours)} icon={Clock} accent="green" />
        {canViewPayroll ? (
          <>
            <StatCard label="Premium pay" value={formatCurrency(totals.premiumPay)} icon={Clock} accent="amber" />
            <StatCard label="Equipment cost" value={formatCurrency(totals.equipmentCost)} icon={Clock} accent="green" />
            <StatCard label="Gross pay" value={formatCurrency(totals.grossPay)} icon={Clock} accent="navy" />
          </>
        ) : null}
        <StatCard
          label="Open exceptions"
          value={totals.openExceptions}
          icon={AlertTriangle}
          accent={totals.openExceptions > 0 ? "amber" : "green"}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <BreakdownTable title="Worker breakdown" rows={period.workerBreakdown} showPayroll={canViewPayroll} />
        <BreakdownTable title="Site breakdown" rows={period.siteBreakdown} showPayroll={canViewPayroll} />
      </div>

      <BreakdownTable title="Equipment usage" rows={period.equipmentBreakdown} showPayroll={false} />

      {period.exceptions.length > 0 ? (
        <CommandCenterCard title="Exceptions">
          <ul className="space-y-2">
            {period.exceptions.map((ex) => (
              <li
                key={ex.id}
                className="flex items-start justify-between gap-3 rounded-md border px-3 py-2 text-sm"
              >
                <div>
                  <p className="font-medium">{ex.message}</p>
                  <p className="text-xs text-muted-foreground">
                    {ex.type} · {ex.status}
                  </p>
                </div>
                <span
                  className={
                    ex.severity === "CRITICAL"
                      ? "text-xs font-semibold text-red-danger"
                      : "text-xs text-muted-foreground"
                  }
                >
                  {ex.severity}
                </span>
              </li>
            ))}
          </ul>
        </CommandCenterCard>
      ) : null}

      {period.auditActivity.length > 0 ? (
        <CommandCenterCard title="Audit activity">
          <ul className="space-y-2">
            {period.auditActivity.map((entry) => (
              <li
                key={entry.id}
                className="flex items-start justify-between gap-3 rounded-md bg-bg-light px-3 py-2 text-sm"
              >
                <div>
                  <p className="font-medium">{entry.action.replace(/\./g, " ")}</p>
                  <p className="text-xs text-muted-foreground">
                    {entry.actorName ?? "System"}
                    {entry.reason ? ` · ${entry.reason}` : null}
                  </p>
                </div>
                <time className="shrink-0 text-xs text-muted-foreground">
                  {new Date(entry.createdAt).toLocaleString()}
                </time>
              </li>
            ))}
          </ul>
        </CommandCenterCard>
      ) : null}

      {unlockOpen ? (
        <dialog
          open
          className="fixed inset-0 z-50 m-auto w-full max-w-md rounded-xl border bg-card-white p-6 shadow-lg backdrop:bg-navy-deep/50"
        >
          <h2 className="text-lg font-semibold text-navy-deep">Unlock pay period</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Unlocking requires an admin reason. Time entries will become editable again.
          </p>
          <div className="mt-4 space-y-2">
            <Label htmlFor="unlockReason">Reason</Label>
            <Input
              id="unlockReason"
              value={unlockReason}
              onChange={(e) => { setUnlockReason(e.target.value); }}
              placeholder="Why is this period being unlocked?"
            />
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setUnlockOpen(false); }} disabled={actionLoading === "unlock"}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleUnlock}
              disabled={actionLoading === "unlock" || !unlockReason.trim()}
            >
              {actionLoading === "unlock" ? "Unlocking…" : "Unlock period"}
            </Button>
          </div>
        </dialog>
      ) : null}
    </div>
  );
}
